import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, renderList as _renderList, normalizeStyle as _normalizeStyle, createBlock as _createBlock, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "inventory-badge__list" }
const _hoisted_2 = {
  key: 0,
  class: "inventory-badge__list"
}
const _hoisted_3 = {
  key: 0,
  class: "forecast-accuracy-setting"
}
const _hoisted_4 = {
  key: 0,
  class: "forecast-accuracy-history-container"
}
const _hoisted_5 = {
  key: 6,
  class: "table-cell table-calendar__container"
}
const _hoisted_6 = {
  key: 0,
  class: "supplier-forecast__controls"
}

import { computed, reactive, ref } from 'vue';
import NumberInputWithComma from '@/corePagesModule/components/NumberInputWithComma.vue';
import AppDatePicker from '@/corePagesModule/components/AppDatePicker.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import NotificationBadge from '@/InventoryControlModule/components/NotificationBadge.vue';
import RecommendTag from '@/corePagesModule/components/RecommendTag.vue';
import InventoryAlertCell from '@/InventoryControlModule/components/InventoryTable/InventoryAlertCell.vue';
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import { ArrivalStatus } from '@/coreDomainsModule/entities/arrival';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { IDate } from '@/coreDomainsModule/date';
import { OrderRepository } from '@/coreInfrastructureModule/Repositories/OrderRepository';
import { i18n } from '@/i18n';
import { diContainer } from '@/coreContainer/container';
import type { IParentBusiness } from '@/InventoryControlModule/domains/entities/Business';
import type { TableType } from '@/InventoryControlModule/composables/useTableState';
import type { IMonthlyBusinessData } from '@/InventoryControlModule/domains/entities/MonthlyData';
import { useTableCalculation } from '@/InventoryControlModule/composables/useTableCalculation';
import { storeToRefs } from 'pinia';
import { useInventoryStore } from '@/InventoryControlModule/pages/useInventoryStore';
import { InventoryTableViewModel } from '@/InventoryControlModule/pages/inventoryTableViewModel';
import { ForecastAccuracyService } from '@/InventoryControlModule/domains/services/forecastAccuracyService';
import { ACCURACY_THRESHOLDS } from '@/InventoryControlModule/constants/forecastAccuracy';
import Mdi from '@/corePagesModule/components/Mdi.vue';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import SupplierForecastSaveButton from './SupplierForecastSaveButton.vue';

/**
 * OrderMonthlyColumn コンポーネントのプロパティ
 */
interface Props {
  /** ヘッダー情報 */
  header: {
    key: string;
    label: string;
    width: string;
    sticky: boolean;
    resizable?: boolean;
  };
  /** 対象の月 (相対月) */
  month: number;
  /** ビジネスアイテム */
  item: IParentBusiness;
  /** 仮想化アイテムタイプ */
  virtualItemType: 'parent' | 'child';
  /** 月次データ */
  monthlyData: Record<string, IMonthlyBusinessData>;
  /** 発注編集データ */
  orderEditData: any;
  /** 非表示のトランザクションヘッダーリスト */
  hiddenTransactionHeaderList: string[];
  /** 開いているビジネスID */
  isOpeningBusinessId: string;
  /** インベントリViewModel */
  inventoryVm: InventoryTableViewModel;
  /** 右端のボーダーを持つか(月別の最後のヘッダーか) */
  hasRightBorder: boolean;
  /** セルがハイライトされているか */
  isHighlighted: boolean;
  /** テーブル数値スタイル関数 */
  tableNumberStyleClass: (business: IParentBusiness, currentOpenId?: string, tableType?: TableType) => string;
  /** 負の値判定関数 */
  isNegative: (value?: number) => boolean;
  /** 近接推奨チェック関数 */
  checkNearestRecommend: (monthlyData: Record<string, IMonthlyBusinessData>, businessId: string, month: number) => boolean;
  /** 到着ステータス取得関数 */
  getArrivalStatusWhenDisplayButton: (monthlyData: Record<string, IMonthlyBusinessData>, businessId: string, month: number) => any;
  /** オーダーデータ確保関数 */
  ensureOrderData: (orderEditData: any, businessId: string, month: number) => void;
  /** 親ビジネス検索関数 */
  findParentBusinessById: (businessId: string) => IParentBusiness | undefined;
  /** 発注編集確定関数 */
  confirmOrderEdit: (orderRepository: OrderRepository, parentBusiness: IParentBusiness, month: number) => void;
  /** オーダー予末在庫計算関数 */
  calcOrderExceptedEnd: (
    monthlyData: Record<string, IMonthlyBusinessData>,
    orderEditData: any,
    item: IParentBusiness,
    month: number,
    forecastAccuracyData?: Record<string, any>
  ) => string;
  /** オーダー在庫回転率計算関数 */
  calcOrderTurnOver: (
    monthlyData: Record<string, IMonthlyBusinessData>,
    orderEditData: any,
    item: IParentBusiness,
    month: number,
    forecastAccuracyData?: Record<string, any>
  ) => string;
}

interface Emits {
  /** 発注数量更新イベント */
  updateAmount: [businessId: string, month: number, amount: number];
  /** 希望納期更新イベント */
  updateDesiredDate: [businessId: string, month: number, date: IDate];
  /** カレンダー入力フラグ更新イベント */
  updateCalendarFlag: [businessId: string, month: number, flag: boolean];
  /** 発注確定イベント */
  confirmOrder: [businessId: string, month: number];
}

type SupplierForecastUiStatus = 'idle' | 'loading' | 'success' | 'error';


export default /*@__PURE__*/_defineComponent({
  __name: 'OrderMonthlyColumn',
  props: {
    header: {},
    month: {},
    item: {},
    virtualItemType: {},
    monthlyData: {},
    orderEditData: {},
    hiddenTransactionHeaderList: {},
    isOpeningBusinessId: {},
    inventoryVm: {},
    hasRightBorder: { type: Boolean },
    isHighlighted: { type: Boolean },
    tableNumberStyleClass: { type: Function },
    isNegative: { type: Function },
    checkNearestRecommend: { type: Function },
    getArrivalStatusWhenDisplayButton: { type: Function },
    ensureOrderData: { type: Function },
    findParentBusinessById: { type: Function },
    confirmOrderEdit: { type: Function },
    calcOrderExceptedEnd: { type: Function },
    calcOrderTurnOver: { type: Function }
  },
  emits: ["updateAmount", "updateDesiredDate", "updateCalendarFlag", "confirmOrder"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const store = useInventoryStore();
const { state } = storeToRefs(store);

/**
 * OrderMonthlyColumn コンポーネントのイベント
 */
const emit = __emit;

// i18n関数
const t = i18n.t;

/**
 * 最適化されたテーブル計算Composable
 * WeakMapキャッシュによる月次データ計算の高速化
 */
const { getMonthlyData } = useTableCalculation();

/**
 * キャッシュされた月次データ
 */
const cachedMonthlyData = computed(() => {
  return getMonthlyData(props.monthlyData, props.item.businessId, props.month);
});

/**
 * 事前計算されたヘッダーキーの最後の部分
 */
const headerKeySuffix = computed(() => {
  return props.header.key.split('-').slice(-1)[0];
});

/**
 * 発注データのキャッシュ付きアクセス
 */
const orderData = computed(() => {
  props.ensureOrderData(props.orderEditData, props.item.businessId, props.month);
  return props.orderEditData[props.item.businessId][props.month];
});

// Recommendedが含まれるかどうか
const hasRecommended = computed(() => {
  const list = cachedMonthlyData.value?.arrivalStatusList;
  return Array.isArray(list) && list.includes(ArrivalStatus.Recommended);
});

/**
 * 親タイプの判定
 */
const isParent = computed(() => props.virtualItemType === 'parent');

/**
 * 計算済みスタイルクラス
 */
const tableNumberStyle = computed(() => {
  return props.tableNumberStyleClass(props.item, props.isOpeningBusinessId, 'virtualized');
});

/**
 * 内示の表示値を計算（設定内示正答率による補正対応）
 * @description 当月以降（month >= 0）で設定内示正答率が設定されている場合、before→afterの形式で表示
 * 親要素・子要素問わず適用される（各要素が個別の設定内示正答率を持つ）
 */
const displayAnnounce = computed(() => {
  const original = cachedMonthlyData.value.announce;
  if (!original) return '--';

  // 当月以降（month >= 0）の場合のみ補正値を表示
  if (props.month >= 0) {
    // 各取引の個別の設定内示正答率を使用
    const settingAccuracy = state.value.forecastAccuracyData[props.item.businessId]?.settingAccuracy;

    if (settingAccuracy && settingAccuracy !== 100) {
      const adjusted = ForecastAccuracyService.calculateAdjustedForecast(original, settingAccuracy);
      return `${original.roundToDecimal(2).addComma()}→${adjusted.roundToDecimal(2).addComma()}`;
    }
  }

  return original.roundToDecimal(2).addComma();
});

// 仕入先内示の入力値をセル単位でキャッシュ
const supplierForecastInputCache = reactive<Record<string, number>>({});

/** 仕入先内示セルのUI状態 */
const supplierForecastUiStatusMap = reactive<Record<string, SupplierForecastUiStatus>>({});

const isSupplierForecastErrorModalOpen = ref(false);

/**
 * セル識別用のキャッシュキーを組み立てる
 * @param businessId - ビジネスID
 * @param month - 月インデックス
 * @returns キャッシュキー
 */
const getSupplierForecastKey = (businessId: string, month: number) => `${businessId}-${month}`;

/**
 * キャッシュされた仕入先内示の入力値を取得
 * @param businessId - ビジネスID
 * @param month - 月インデックス
 * @param fallback - キャッシュ未設定時の初期値
 * @returns キャッシュ済みまたは初期値
 */
const getSupplierForecastInput = (businessId: string, month: number, fallback: number) => {
  const key = getSupplierForecastKey(businessId, month);
  if (!(key in supplierForecastInputCache)) supplierForecastInputCache[key] = fallback;
  return supplierForecastInputCache[key] ?? fallback;
};

/**
 * 仕入先内示の入力値をキャッシュに保存
 * @param businessId - ビジネスID
 * @param month - 月インデックス
 * @param val - 保存する値
 */
const setSupplierForecastInput = (businessId: string, month: number, val: number) => {
  supplierForecastInputCache[getSupplierForecastKey(businessId, month)] = val;
};

/**
 * 仕入先内示の入力値が保存済みと異なるか判定する
 * @param businessId - ビジネスID
 * @param month - 月インデックス
 * @param origin - 保存済みの値
 * @returns 値に変更があればtrue
 */
const hasSupplierForecastChanged = (businessId: string, month: number, origin: number): boolean => {
  const current = getSupplierForecastInput(businessId, month, origin);
  return !Object.is(current, origin);
};

/**
 * UI状態が未初期化なら生成して返す
 * @param key - セルを一意に識別するキー
 */
/**
 * セルごとのUI状態を更新する
 * @param key - セルを一意に識別するキー
 * @param status - 設定するUI状態
 */
const setSupplierForecastUiStatusByKey = (key: string, status: SupplierForecastUiStatus) => {
  supplierForecastUiStatusMap[key] = status;
};

/**
 * セルごとのUI状態を取得する
 * @param businessId - ビジネスID
 * @param month - 対象月
 */
const getSupplierForecastUiStatus = (businessId: string, month: number): SupplierForecastUiStatus => {
  const key = getSupplierForecastKey(businessId, month);
  return supplierForecastUiStatusMap[key] ?? 'idle';
};

/**
 * 編集を無効化すべきか判定する
 * @param businessId - ビジネスID
 * @param month - 対象月
 */
const isSupplierForecastDisabled = (businessId: string, month: number): boolean => {
  const status = getSupplierForecastUiStatus(businessId, month);
  return status === 'loading' || status === 'success';
};

/**
 * コンポーネントから通知されたUI状態を保存する
 * @param businessId - ビジネスID
 * @param month - 月インデックス
 * @param status - 新しいUI状態
 */
const handleSupplierForecastStatusChange = (businessId: string, month: number, status: SupplierForecastUiStatus) => {
  setSupplierForecastUiStatusByKey(getSupplierForecastKey(businessId, month), status);
};

/**
 * 保存処理で発生した例外をログ出力する
 * @param error - 発生した例外
 */
const handleSupplierForecastError = async (error: unknown) => {
  console.error(error);
  if (isSupplierForecastErrorModalOpen.value) return;

  isSupplierForecastErrorModalOpen.value = true;
  try {
    await showInfoModal({
      modalTitle: t('エラーが発生しました'),
      modalDescription: t('仕入れ先内示の保存に失敗しました。時間をおいて再度お試しください。'),
      backButtonLabel: t('閉じる'),
      icon: 'error',
      colorStyle: 'error',
      enableOutsideClick: false,
      isDisabledNextButton: true,
    });
  } finally {
    isSupplierForecastErrorModalOpen.value = false;
  }
};

/**
 * 仕入先内示の値を保存して状態を更新
 * @param businessId - ビジネスID
 * @param month - 月インデックス
 * @param origin - 元の値
 */
const saveSupplierForecast = async (businessId: string, month: number, origin: number) => {
  const amount = getSupplierForecastInput(businessId, month, origin);
  const result = await diContainer.upsertTransactionValuesUseCase.Execute(businessId, [
    {
      type: 'supplierForecast',
      month,
      value: amount,
      originValue: origin,
    },
  ]);
  if (result.status.type === 'success') {
    setSupplierForecastInput(businessId, month, amount);
    await store.refreshBusiness(businessId);
    return;
  }
  if (result.status.type === 'error') {
    throw result.status.error;
  }
  throw new Error('failed to save supplier forecast');
};

/**
 * 数値表示において、undefined/NaN/(0)かそうでないかを判定して表示内容を返す関数
 * @param value - 判定する数値
 * @param needsExceptZero - 0を除外するかどうか
 */
const getAmountDisplayContent = (value: number | undefined, needsExceptZero = false) => {
  return value === undefined || Number.isNaN(value) || (needsExceptZero && value === 0) ? '--' : value.roundToDecimal(2).addComma();
};

/**
 * 前月内示正答率の表示値を取得
 * @param item - ビジネスアイテム
 * @returns 表示文字列
 */
const getPreviousMonthAccuracyDisplay = (item: IParentBusiness): string => {
  // 前月（-1月）のデータを取得
  const previousMonthData = getMonthlyData(state.value.monthlyData, item.businessId, -1);
  if (!previousMonthData) return '--';

  const accuracy = ForecastAccuracyService.calculatePreviousMonthAccuracy(
    previousMonthData.announce || 0,
    previousMonthData.salesResult || 0
  );

  return accuracy !== undefined ? `${getAmountDisplayContent(accuracy)}%` : '--';
};

/**
 * 前月内示正答率の色分けクラスを取得
 * @param item - ビジネスアイテム
 * @returns CSSクラス名
 */
const getPreviousMonthAccuracyClass = (item: IParentBusiness): string => {
  const previousMonthData = getMonthlyData(state.value.monthlyData, item.businessId, -1);
  if (!previousMonthData) return '';

  const accuracy = ForecastAccuracyService.calculatePreviousMonthAccuracy(
    previousMonthData.announce || 0,
    previousMonthData.salesResult || 0
  );

  if (!accuracy) return '';

  if (accuracy <= ACCURACY_THRESHOLDS.CRITICAL) {
    return 'forecast-accuracy--critical';
  } else if (accuracy > ACCURACY_THRESHOLDS.CRITICAL && accuracy <= ACCURACY_THRESHOLDS.WARNING) {
    return 'forecast-accuracy--warning';
  }
  return '';
};

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createBlock(EzCell, {
    "is-number": "",
    "is-highlighted": _ctx.isHighlighted,
    "is-negative": _ctx.isNegative(cachedMonthlyData.value[headerKeySuffix.value]),
    class: _normalizeClass(tableNumberStyle.value),
    "has-right-border": _ctx.hasRightBorder
  }, {
    default: _withCtx(() => [
      (!_ctx.hiddenTransactionHeaderList.includes(_ctx.header.key))
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            (_ctx.header.key.includes('-alreadyArrival') && isParent.value)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createTextVNode(_toDisplayString(getAmountDisplayContent(cachedMonthlyData.value.deliveredArrival, true)), 1)
                ], 64))
              : (_ctx.header.key.includes('-expectedArrival') && isParent.value)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 1,
                    class: _normalizeClass(["inventory-badge__container", tableNumberStyle.value])
                  }, [
                    (hasRecommended.value)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                          _createVNode(_component_router_link, {
                            to: { name: 'recommend-order', params: { id: _ctx.item.businessId } },
                            target: "_blank"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(RecommendTag, {
                                status: _unref(ArrivalStatus).Recommended
                              }, null, 8, ["status"])
                            ], undefined, true),
                            _: 1
                          }, 8, ["to"]),
                          _createElementVNode("div", _hoisted_1, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(cachedMonthlyData.value.arrivalStatusList, (arrivalStatus) => {
                              return (_openBlock(), _createElementBlock(_Fragment, {
                                key: `${_ctx.item.businessId}-${arrivalStatus}`
                              }, [
                                (arrivalStatus !== _unref(ArrivalStatus).Recommended && arrivalStatus !== _unref(ArrivalStatus).Nothing)
                                  ? (_openBlock(), _createBlock(NotificationBadge, {
                                      key: 0,
                                      class: "inventory-badge",
                                      style: _normalizeStyle(_ctx.inventoryVm.notificationBadgeStyle(arrivalStatus, true)),
                                      "arrival-status": arrivalStatus,
                                      unit: _ctx.item.unit,
                                      "un-paid-arrivals": cachedMonthlyData.value.unPaidArrival,
                                      "approval-requested-arrival": cachedMonthlyData.value.approvalRequestedArrival
                                    }, null, 8, ["style", "arrival-status", "unit", "un-paid-arrivals", "approval-requested-arrival"]))
                                  : _createCommentVNode("", true)
                              ], 64))
                            }), 128))
                          ])
                        ], 64))
                      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                          ((cachedMonthlyData.value.exceptedArrivalForOrderMode ?? 0) > 0)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(cachedMonthlyData.value.arrivalStatusList, (arrivalStatus) => {
                                  return (_openBlock(), _createElementBlock(_Fragment, {
                                    key: `${_ctx.item.businessId}-${arrivalStatus}`
                                  }, [
                                    (arrivalStatus !== _unref(ArrivalStatus).Nothing)
                                      ? (_openBlock(), _createBlock(NotificationBadge, {
                                          key: 0,
                                          class: "inventory-badge",
                                          style: _normalizeStyle(_ctx.inventoryVm.notificationBadgeStyle(arrivalStatus, true)),
                                          "arrival-status": arrivalStatus,
                                          unit: _ctx.item.unit,
                                          "un-paid-arrivals": cachedMonthlyData.value.unPaidArrival,
                                          "approval-requested-arrival": cachedMonthlyData.value.approvalRequestedArrival
                                        }, null, 8, ["style", "arrival-status", "unit", "un-paid-arrivals", "approval-requested-arrival"]))
                                      : _createCommentVNode("", true)
                                  ], 64))
                                }), 128))
                              ]))
                            : _createCommentVNode("", true),
                          _createTextVNode(" " + _toDisplayString(getAmountDisplayContent(cachedMonthlyData.value.exceptedArrivalForOrderMode, true)), 1)
                        ], 64))
                  ], 2))
                : (_ctx.month === 0 && _ctx.header.key.includes('-previousMonthAccuracy'))
                  ? (_openBlock(), _createElementBlock("div", {
                      key: 2,
                      class: _normalizeClass(getPreviousMonthAccuracyClass(_ctx.item))
                    }, _toDisplayString(getPreviousMonthAccuracyDisplay(_ctx.item)), 3))
                  : (_ctx.month === 0 && _ctx.header.key.includes('-settingAccuracy'))
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                        (_ctx.virtualItemType === 'parent')
                          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                              _createVNode(NumberInputWithComma, {
                                "model-value": _unref(state).forecastAccuracyData[_ctx.item.businessId]?.settingAccuracy || 100,
                                class: "table-input",
                                suffix: "%",
                                onInput: _cache[0] || (_cache[0] = (val) => _unref(store).updateForecastAccuracySettingAccuracy(_ctx.item.businessId, parseFloat(val)))
                              }, null, 8, ["model-value"])
                            ]))
                          : _createCommentVNode("", true)
                      ], 64))
                    : (_ctx.month === 0 && _ctx.header.key.includes('-accuracyHistory'))
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 4 }, [
                          (_ctx.virtualItemType === 'parent')
                            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                                _createElementVNode("button", {
                                  class: "forecast-accuracy-history-btn",
                                  onClick: _cache[1] || (_cache[1] = () => _unref(store).openForecastAccuracyHistoryModal(_ctx.item.businessId))
                                }, [
                                  _createVNode(SvgLoader, { name: "history" })
                                ])
                              ]))
                            : _createCommentVNode("", true)
                        ], 64))
                      : (_ctx.header.key.includes('-amount') && _ctx.virtualItemType === 'parent')
                        ? (_openBlock(), _createBlock(NumberInputWithComma, {
                            key: 5,
                            "model-value": (_ctx.ensureOrderData(_ctx.orderEditData, _ctx.item.businessId, _ctx.month), _ctx.orderEditData[_ctx.item.businessId][_ctx.month].amount),
                            class: _normalizeClass(["table-input", `table-input--${_ctx.orderEditData[_ctx.item.businessId][_ctx.month].status}`]),
                            onInput: _cache[2] || (_cache[2] = 
            (val) => {
              _ctx.ensureOrderData(_ctx.orderEditData, _ctx.item.businessId, _ctx.month);
              emit('updateAmount', _ctx.item.businessId, _ctx.month, parseFloat(val));
            }
          )
                          }, null, 8, ["model-value", "class"]))
                        : (_ctx.header.key.includes('-desiredDeliveryDate') && _ctx.virtualItemType === 'parent')
                          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                              (_ctx.orderEditData[_ctx.item.businessId][_ctx.month].isEmptyCalendarInputClickedOnce || _ctx.orderEditData[_ctx.item.businessId][_ctx.month].status)
                                ? (_openBlock(), _createBlock(AppDatePicker, {
                                    key: 0,
                                    "model-value": _unref(DateService).convertIDateToDate(_ctx.orderEditData[_ctx.item.businessId][_ctx.month].crossDate as IDate),
                                    class: _normalizeClass(`table-input table-input--${_ctx.orderEditData[_ctx.item.businessId][_ctx.month].status}`),
                                    "mode-height": 1,
                                    format: "MM/dd",
                                    "enable-time-picker": false,
                                    "month-change-on-scroll": false,
                                    teleport: true,
                                    "week-start": "0",
                                    locale: _unref(i18n).locale,
                                    placeholder: _unref(t)('日付を選択'),
                                    "select-text": _unref(t)('選択'),
                                    "cancel-text": _unref(t)('キャンセル'),
                                    "show-now-button": true,
                                    clearable: false,
                                    "now-button-label": _unref(t)('現在を選択'),
                                    pad: "",
                                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (emit('updateDesiredDate', _ctx.item.businessId, _ctx.month, _unref(DateService).convertDateToIDate($event))))
                                  }, null, 8, ["model-value", "class", "locale", "placeholder", "select-text", "cancel-text", "now-button-label"]))
                                : (_openBlock(), _createElementBlock("button", {
                                    key: 1,
                                    class: "table-calendar__default",
                                    onClick: _cache[4] || (_cache[4] = ($event: any) => (emit('updateCalendarFlag', _ctx.item.businessId, _ctx.month, true)))
                                  }, [
                                    _createVNode(Mdi, {
                                      class: "table-calendar__default__icon",
                                      icon: "calendar_today"
                                    })
                                  ]))
                            ]))
                          : (_ctx.header.key.includes('-createOrder') && _ctx.virtualItemType === 'parent')
                            ? (_openBlock(), _createBlock(TextButton, {
                                key: 7,
                                class: "table-button table-button--confirm",
                                "button-style": "btn-primary",
                                "on-click": 
            () => {
              _ctx.ensureOrderData(_ctx.orderEditData, _ctx.item.businessId, _ctx.month);
              emit('confirmOrder', _ctx.item.businessId, _ctx.month);
            }
          
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(_unref(t)('確定')), 1)
                                ], undefined, true),
                                _: 1
                              }, 8, ["on-click"]))
                            : (_ctx.header.key.includes('-expectedEndInventory') && _ctx.virtualItemType === 'parent')
                              ? (_openBlock(), _createElementBlock(_Fragment, { key: 8 }, [
                                  _createTextVNode(_toDisplayString(_ctx.calcOrderExceptedEnd(_ctx.monthlyData, _ctx.orderEditData, _ctx.item, _ctx.month, _unref(state).forecastAccuracyData)), 1)
                                ], 64))
                              : (_ctx.header.key.includes('-turnOver') && _ctx.virtualItemType === 'parent')
                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 9 }, [
                                    _createTextVNode(_toDisplayString(_ctx.calcOrderTurnOver(_ctx.monthlyData, _ctx.orderEditData, _ctx.item, _ctx.month, _unref(state).forecastAccuracyData)), 1)
                                  ], 64))
                                : (_ctx.header.key.includes('-alert') && _ctx.virtualItemType === 'parent')
                                  ? (_openBlock(), _createBlock(InventoryAlertCell, {
                                      key: 10,
                                      "prop-business": _ctx.item,
                                      "prop-relative-month": _ctx.month
                                    }, null, 8, ["prop-business", "prop-relative-month"]))
                                  : (_openBlock(), _createElementBlock(_Fragment, { key: 11 }, [
                                      (
            _ctx.virtualItemType === 'parent' ||
            _ctx.header.key.includes('-announce') ||
            _ctx.header.key.includes('-salesResult') ||
            _ctx.header.key.includes('-supplierAnnounce')
          )
                                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                            (_ctx.header.key.includes('-announce'))
                                              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                  _createTextVNode(_toDisplayString(displayAnnounce.value), 1)
                                                ], 64))
                                              : (_ctx.header.key.includes('-supplierAnnounce'))
                                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                                    (_ctx.virtualItemType === 'parent' && _ctx.month >= 0)
                                                      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                                                          _createVNode(NumberInputWithComma, {
                                                            "model-value": getSupplierForecastInput(_ctx.item.businessId, _ctx.month, cachedMonthlyData.value.supplierAnnounce || 0),
                                                            class: "table-input",
                                                            disabled: isSupplierForecastDisabled(_ctx.item.businessId, _ctx.month),
                                                            onInput: _cache[5] || (_cache[5] = (val) => setSupplierForecastInput(_ctx.item.businessId, _ctx.month, parseFloat(val)))
                                                          }, null, 8, ["model-value", "disabled"]),
                                                          _createVNode(SupplierForecastSaveButton, {
                                                            "save-handler": () => saveSupplierForecast(_ctx.item.businessId, _ctx.month, cachedMonthlyData.value.supplierAnnounce || 0),
                                                            disabled: isSupplierForecastDisabled(_ctx.item.businessId, _ctx.month),
                                                            "can-save": hasSupplierForecastChanged(_ctx.item.businessId, _ctx.month, cachedMonthlyData.value.supplierAnnounce || 0),
                                                            "aria-label": _unref(t)('保存'),
                                                            "loading-aria-label": _unref(t)('保存中'),
                                                            "success-aria-label": _unref(t)('保存完了'),
                                                            "error-aria-label": _unref(t)('保存に失敗しました。再度保存するには選択してください'),
                                                            onStatusChange: _cache[6] || (_cache[6] = (status) => handleSupplierForecastStatusChange(_ctx.item.businessId, _ctx.month, status)),
                                                            onError: handleSupplierForecastError
                                                          }, null, 8, ["save-handler", "disabled", "can-save", "aria-label", "loading-aria-label", "success-aria-label", "error-aria-label"])
                                                        ]))
                                                      : (_ctx.virtualItemType !== 'parent')
                                                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                                            _createTextVNode("--")
                                                          ], 64))
                                                        : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                                            _createTextVNode(_toDisplayString(getAmountDisplayContent(cachedMonthlyData.value[_ctx.header.key.split('-').slice(-1)[0]])), 1)
                                                          ], 64))
                                                  ], 64))
                                                : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                                    _createTextVNode(_toDisplayString(getAmountDisplayContent(cachedMonthlyData.value[_ctx.header.key.split('-').slice(-1)[0]])), 1)
                                                  ], 64))
                                          ], 64))
                                        : (
            !_ctx.header.key.includes('-amount') && !_ctx.header.key.includes('-desiredDeliveryDate') && !_ctx.header.key.includes('-createOrder')
          )
                                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                              _createTextVNode(" -- ")
                                            ], 64))
                                          : _createCommentVNode("", true)
                                    ], 64))
          ], 64))
        : _createCommentVNode("", true)
    ], undefined, true),
    _: 1
  }, 8, ["is-highlighted", "is-negative", "class", "has-right-border"]))
}
}

})